import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import AssignInstagramLabelDialog from "@saleor/inslabel/components/AssignInstagramLabelDialog";
import InstagramLabelDetailsPage, {
  InstagramLabelDetailsPageFormData
} from "@saleor/inslabel/components/InstagramLabelDetailsPage";
import {
  useInstagramLabelUpdate,
  useInstagramVisibleLabelInsMutation,
  useLabelInsReorderMutation,
  useLabelNotTopInsReorderMutation,
  useLabelSortLabelinsMutation,
  useLabelUnsortLabelinsMutation
} from "@saleor/inslabel/mutations";
import { useInstagramLabel } from "@saleor/inslabel/queries";
import {
  inslabelListUrl,
  inslabelUrl,
  InstagramLabelPathUrlQueryParams,
  InstagramLabelUrlDialog
} from "@saleor/inslabel/urls";
import { instagramUrl } from "@saleor/instagram/urls";
import { commonMessages } from "@saleor/intl";
import { ReorderEvent } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { move } from "@saleor/utils/lists";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface InsgramLabelDetailsProps {
  id: string;
  params: InstagramLabelPathUrlQueryParams;
}

export const InstagramLabelDetails: React.FC<InsgramLabelDetailsProps> = ({
  id,
  params
}) => {
  const intl = useIntl();

  const navigate = useNavigator();

  const notify = useNotifier();

  const { data, loading, refetch } = useInstagramLabel({
    variables: { id }
  });

  const [updateInsLabel, updateInsLabelOpts] = useInstagramLabelUpdate({
    onCompleted: data => {
      if (data.instagramLabelUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const handleSubmit = async (data: InstagramLabelDetailsPageFormData) => {
    const result = await updateInsLabel({
      variables: {
        id,
        input: {
          name: data.name
        }
      }
    });
    return result.data.instagramLabelUpdate.errors;
  };

  const [labelInsReorder] = useLabelInsReorderMutation({
    onCompleted: data => {
      if (data.labelReorderInstagram.errors.length !== 0) {
        notify({
          status: "error",
          text: "error"
        });
      }
    }
  });
  const [labelNotTopInsReorder] = useLabelNotTopInsReorderMutation({
    onCompleted: data => {
      if (data.labelReorderNottopInstagram.errors.length !== 0) {
        notify({
          status: "error",
          text: "error"
        });
      }
    }
  });

  const { reset } = useBulkActions(params.ids);

  const [
    labelDeleteLabelins,
    labelDeleteLabelinsOpts
  ] = useLabelUnsortLabelinsMutation({
    onCompleted: data => {
      if (data.labelUnsortLabelins.errors.length === 0) {
        closeModal();
        reset();
        refetch();
      }
    }
  });

  const handleInsReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    labelInsReorder({
      optimisticResponse: {
        labelReorderInstagram: {
          __typename: "LabelReorderInstagram",
          label: {
            ...data.instagramLabel,
            instagramList: move(
              data.instagramLabel.instagramList[oldIndex],
              data.instagramLabel.instagramList,
              (a, b) => a.id === b.id,
              newIndex
            )
          },
          errors: []
        }
      },
      variables: {
        id,
        move: {
          id: data.instagramLabel.instagramList[oldIndex].id,
          sortOrder: newIndex - oldIndex
        }
      }
    });

  const handleNotTopInsReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    labelNotTopInsReorder({
      optimisticResponse: {
        labelReorderNottopInstagram: {
          __typename: "LabelReorderNotTopInstagram",
          label: {
            ...data.instagramLabel,
            instagramList: move(
              data.instagramLabel.instagramUnsortList[oldIndex],
              data.instagramLabel.instagramUnsortList,
              (a, b) => a.id === b.id,
              newIndex
            )
          },
          errors: []
        }
      },
      variables: {
        id,
        move: {
          id: data.instagramLabel.instagramUnsortList[oldIndex].id,
          sortOrder: newIndex - oldIndex
        }
      }
    });
  const [openModal, closeModal] = createDialogActionHandlers<
    InstagramLabelUrlDialog,
    InstagramLabelPathUrlQueryParams
  >(navigate, params => inslabelUrl(id, params), params);

  const [assignLabelInstagram] = useLabelSortLabelinsMutation({
    onCompleted: data => {
      if (data.labelSortLabelins.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added Content to Label"
          })
        });
        navigate(inslabelUrl(id), true);
      }
    }
  });

  const [visibleLabelIns] = useInstagramVisibleLabelInsMutation({
    onCompleted: data => {
      if (data.instagramVisibleLabelIns.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Changed Content Visible"
          })
        });
        navigate(inslabelUrl(id), true);
      }
    }
  });

  const [visibleLabelNoTopIns] = useInstagramVisibleLabelInsMutation({
    onCompleted: data => {
      if (data.instagramVisibleLabelIns.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Changed Content Visible"
          })
        });
      }
    }
  });

  const [onInsSelectReorder] = useLabelInsReorderMutation({
    onCompleted: data => {
      if (data.labelReorderInstagram.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Reorder Content"
          })
        });
        navigate(inslabelUrl(id), true);
      }
    }
  });

  const handleInsSelectReorder = (insId: string, sortOrder: number) => {
    onInsSelectReorder({
      variables: {
        id,
        move: {
          id: insId,
          sortOrder
        }
      }
    });
  };

  const [onNotTopInsSelectReorder] = useLabelNotTopInsReorderMutation({
    onCompleted: data => {
      if (data.labelReorderNottopInstagram.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Reorder Content"
          })
        });
      }
    }
  });

  const handleNotTopInsSelectReorder = (insId: string, sortOrder: number) => {
    onNotTopInsSelectReorder({
      variables: {
        id,
        move: {
          id: insId,
          sortOrder
        }
      }
    });
  };

  return (
    <>
      <InstagramLabelDetailsPage
        label={data?.instagramLabel}
        disabled={loading}
        onSubmit={handleSubmit}
        onBack={() => navigate(inslabelListUrl())}
        saveButtonBarState={updateInsLabelOpts.status}
        labelIns={data?.instagramLabel.instagramSortList}
        reverseVisible={instagrams =>
          visibleLabelIns({
            variables: {
              ids: instagrams.map(instagram => instagram.id)
            }
          })
        }
        onInsReorder={handleInsReorder}
        onDeleteIns={id =>
          openModal("remove-labelins", {
            id
          })
        }
        onInsSelectReorder={handleInsSelectReorder}
        onAddIns={() => openModal("assign")}
        onRowClick={id => {
          navigate(instagramUrl(id));
        }}
      />

      <ActionDialog
        open={params.action === "remove-labelins"}
        onClose={closeModal}
        confirmButtonState={labelDeleteLabelinsOpts.status}
        onConfirm={() =>
          labelDeleteLabelins({
            variables: {
              id: params.id
            }
          })
        }
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Label Content",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to cancel the sorting of this content in this Label?"
            description="dialog content"
          />
        </DialogContentText>
      </ActionDialog>

      <AssignInstagramLabelDialog
        instargrams={data?.instagramLabel?.instagramUnsortList}
        onRowClick={id => () => navigate(instagramUrl(id))}
        open={params.action === "assign"}
        onClose={closeModal}
        onSubmit={instagrams =>
          assignLabelInstagram({
            variables: {
              ids: instagrams.map(instagram => instagram.id)
            }
          })
        }
        onInsReorder={handleNotTopInsReorder}
        onInsSelectReorder={handleNotTopInsSelectReorder}
        reverseVisible={instagrams =>
          visibleLabelNoTopIns({
            variables: {
              ids: instagrams.map(instagram => instagram.id)
            }
          })
        }
      />
    </>
  );
};
export default InstagramLabelDetails;
